import * as jsondiffpatch from 'jsondiffpatch';
import { App, Project, Site } from '../../models';
import { DifferencesObject } from '../../services/logic-services/models';
import { FIELD_TYPE_EMPTY_VALUES_CONSTANTS } from '../constants';
import { PatchTypes } from '../enums';
import { FieldType } from '../enums/field-type.enum';
import { MapFieldPoint } from './../../models/map-field-point';

export class DifferenceLogicUtility {
    private static readonly KENDO_PROPERTIES = ['extension', 'state', 'uid', 'uniqueFileName'];
    private static readonly MAP_FIELD_PREFIX = 'mapFieldPoint-';

    // Mapfieldpoint is treated as array type, but handled via prefix checks as it's not part of jsonforms
    private static readonly ARRAY_FIELD_TYPES = [
        FieldType.Grid,
        FieldType.Person,
        FieldType.PhotoGallery,
        FieldType.Uploader
    ];

    differenceObject: DifferencesObject;
    patchType = '';
    multipleValueUpdate = false;
    jsonFormsId = '';
    isRepositioningGrid = false;
    hasMultipleArrayItemChanges = false;
    hasMultipleFieldChanges = false;

    private gridRowIndex: number | null = null;
    private gridColumnId: string | null = null;
    private gridRowId: string | null = null;

    private readonly project: Project;
    private readonly originalSiteData: object;
    private readonly updatedSite: Site;
    private readonly mapFieldPoint: MapFieldPoint;
    private readonly fieldMap: Map<string, any>;
    private readonly jsonDiffPatch: jsondiffpatch.DiffPatcher;

    constructor(app: App, project: Project, site: Site, mapFieldPoint: MapFieldPoint, originalSiteData: object) {
        this.differenceObject = new DifferencesObject();
        this.differenceObject.appId = app.id;
        this.differenceObject.projectId = project?.id ?? '';
        this.differenceObject.siteId = site.id;
        this.differenceObject.path = '';
        this.differenceObject.isPathValid = false;
        this.patchType = PatchTypes.AddUpdate;
        this.project = project;
        this.mapFieldPoint = mapFieldPoint;
        this.fieldMap = this.createFlattenedFieldMap();
        this.originalSiteData = originalSiteData;
        this.updatedSite = site;
        this.jsonDiffPatch = jsondiffpatch.create();
    }

    // This utility is only designed to handle one changed field at a time if they are multiple
    generateDifferencesObject(): DifferencesObject | null {
        const filteredNewObject = this.filterObjectByProjectFields(this.updatedSite.data || {});
        const filteredOldObject = this.filterObjectByProjectFields(this.originalSiteData || {});

        this.resetMultipleValuesSettings();
        this.deleteKendoProperties(filteredNewObject);
        this.deleteKendoProperties(filteredOldObject);

        const delta = this.generateDiffPatchDifferences(filteredOldObject, filteredNewObject);
        const changedFieldIds = this.getChangedFieldIds(delta);

        if (changedFieldIds.length !== 1) {
            this.hasMultipleFieldChanges = changedFieldIds.length > 1;
            return null;
        }

        const fieldId = changedFieldIds[0];
        const fieldDelta = (delta as any)?.[fieldId];

        this.configureMultipleValuesLogic(fieldId, filteredNewObject, filteredOldObject, fieldDelta);

        if (this.hasMultipleArrayItemChanges) {
            return null;
        }

        if (this.multipleValueUpdate) {
            this.multipleValues(filteredNewObject);
        }
        else {
            this.findObjectDifferences(filteredNewObject, filteredOldObject);
        }

        this.setPathValidity();

        return this.differenceObject;
    }

    static valuesAreTheSame(value1: any, value2: any): boolean {
        return JSON.stringify(value1) === JSON.stringify(value2);
    }

    private resetMultipleValuesSettings(): void {
        this.multipleValueUpdate = false;
        this.jsonFormsId = '';
        this.isRepositioningGrid = false;
        this.hasMultipleArrayItemChanges = false;
        this.hasMultipleFieldChanges = false;
        this.differenceObject.isPathValid = false;
        this.gridRowIndex = null;
        this.gridColumnId = null;
        this.gridRowId = null;
    }

    private deleteKendoProperties(obj: object): void {
        for (const key in obj) {
            if (Array.isArray(obj[key])) {
                obj[key].forEach((value: object) => {
                    this.deleteKendoProperties(value);
                });
            }

            for (const property of DifferenceLogicUtility.KENDO_PROPERTIES) {
                if (obj.hasOwnProperty(property)) {
                    delete obj[property];
                }
            }
        }
    }

    private multipleValues(newObject: object): void {
        this.differenceObject.fieldId = this.jsonFormsId;

        if (this.gridRowIndex !== null && this.gridColumnId !== null) {
            this.differenceObject.path = `/${this.jsonFormsId}/${this.gridRowIndex}/${this.gridColumnId}`;

            const gridData = newObject[this.jsonFormsId] as any[];
            if (gridData?.[this.gridRowIndex]) {
                this.differenceObject.value[this.gridColumnId] = gridData[this.gridRowIndex][this.gridColumnId];
            }

            if (this.gridRowId) {
                this.differenceObject.objectId = this.gridRowId;
            }

            this.differenceObject.objectFieldId = this.gridColumnId;
        }
        else {
            this.differenceObject.path = `/${this.jsonFormsId}`;
            this.differenceObject.value[this.jsonFormsId] = newObject[this.jsonFormsId];
        }

        if (this.differenceObject.value.length === 0) {
            this.patchType = PatchTypes.Remove;
        }
    }

    private getAllKeys(newObject: object, oldObject: object): string[] {
        return Array.from(new Set([...Object.keys(newObject || {}), ...Object.keys(oldObject || {})]));
    }

    private findObjectDifferences(newObject: object, oldObject: object): void {
        const allKeys = this.getAllKeys(newObject, oldObject);

        for (const key of allKeys) {
            if (DifferenceLogicUtility.valuesAreTheSame(newObject[key], oldObject[key])) {
                continue;
            }

            if (this.differenceObject.fieldId) {
                this.differenceObject.objectFieldId = key;
                this.differenceObject.value[key] = newObject[key];
                this.differenceObject.path = `${this.differenceObject.path}/${key}`;
                return;
            }

            if (this.differenceObject.fieldId === undefined) {
                this.differenceObject.fieldId = key;
                this.differenceObject.path = `${this.differenceObject.path}/${key}`;
            }

            if (Array.isArray(newObject[key])) {
                const isNestedArrayType = this.isNestedArrayType(key);
                if (isNestedArrayType) {
                    this.differenceObject.value[key] = newObject[key];
                }
                else {
                    this.findArrayDifferences(key, newObject[key], oldObject[key]);
                }
            }
            else {
                this.differenceObject.value[key] = newObject[key];
            }

            if (this.shouldTreatAsRemoval(this.differenceObject.value[key])) {
                this.patchType = PatchTypes.Remove;
            }
        }
    }

    private shouldTreatAsRemoval(value: any): boolean {
        return !this.differenceObject.objectId &&
            (value === undefined ||
                value === "" ||
                value === FIELD_TYPE_EMPTY_VALUES_CONSTANTS.TextAreaEmptyParagraphTag);
    }

    private findArrayDifferences(arrayKey: string, newArray: any[], oldArray: any[]): void {
        this.patchType = (newArray?.length < oldArray?.length) ? PatchTypes.Remove : PatchTypes.AddUpdate;

        if (newArray?.length !== oldArray?.length) {
            if (oldArray === undefined) {
                this.differenceObject.value[arrayKey] = newArray;
                return;
            }

            this.differenceObject.path = `${this.differenceObject.path}/0`;
            this.differenceObject.value[arrayKey] = newArray[0];
            return;
        }

        newArray.forEach((value, index) => {
            if (DifferenceLogicUtility.valuesAreTheSame(value, oldArray[index])) {
                return;
            }

            if (this.differenceObject.fieldId) {
                this.differenceObject.objectId = value['id'];
                this.differenceObject.path = `${this.differenceObject.path}/${index}`;
            }

            this.findObjectDifferences(value, oldArray[index]);
        });
    }

    private getChangedFieldIds(delta: jsondiffpatch.Delta | undefined): string[] {
        if (!delta) {
            return [];
        }
        return Object.keys(delta).filter(k => k !== '_t');
    }

    private checkForMultipleChangesInArrayItem(fieldDelta: any, newValue: any, oldValue: any): boolean {
        if (!Array.isArray(newValue) || !Array.isArray(oldValue) || !fieldDelta || typeof fieldDelta !== 'object') {
            return false;
        }

        let changedRowCount = 0;

        for (const key in fieldDelta) {
            if (key === '_t') {
                continue;
            }

            const itemDelta = fieldDelta[key];
            if (itemDelta && typeof itemDelta === 'object' && !Array.isArray(itemDelta)) {
                // Count field changes: total keys minus '_t' marker if present
                const fieldCount = Object.keys(itemDelta).length - (itemDelta._t ? 1 : 0);
                if (fieldCount > 1) {
                    return true;
                }

                if (fieldCount > 0) {
                    changedRowCount++;
                }
            }
        }

        return changedRowCount > 1;
    }

    private createFlattenedFieldMap(): Map<string, any> {
        const fieldMap = new Map<string, any>();

        if (this.project?.JSONForms?.Sections) {
            this.flattenSections(this.project.JSONForms.Sections, fieldMap);
        }

        this.flattenMapFieldPoints(fieldMap);

        return fieldMap;
    }

    private generateDiffPatchDifferences(oldObj: object, newObj: object): jsondiffpatch.Delta | undefined {
        return this.jsonDiffPatch.diff(oldObj, newObj);
    }

    private flattenSections(sections: any[], fieldMap: Map<string, any>): void {
        for (const section of sections) {
            if (section.Columns) {
                for (const column of section.Columns) {
                    if (column.Fields) {
                        for (const field of column.Fields) {
                            fieldMap.set(field.id, field);

                            if (field.SelectOption === FieldType.Grid && field.FieldType?.options?.columns) {
                                for (const gridColumn of field.FieldType.options.columns) {
                                    const gridColumnField = {
                                        id: gridColumn.id,
                                        SelectOption: this.getGridColumnFieldType(gridColumn.type),
                                        type: gridColumn.type,
                                        header: gridColumn.header,
                                        parentGridId: field.id
                                    };
                                    fieldMap.set(gridColumn.id, gridColumnField);
                                }
                            }
                        }
                    }
                }
            }
            if (section.Sections) {
                this.flattenSections(section.Sections, fieldMap);
            }
        }
    }

    private flattenMapFieldPoints(fieldMap: Map<string, any>): void {
        if (!this.mapFieldPoint?.columns) {
            return;
        }

        const convertedMapFieldPoint = this.convertMapFieldPointToGrid(this.mapFieldPoint);

        fieldMap.set(this.mapFieldPoint.id, convertedMapFieldPoint);
        for (const gridColumn of this.mapFieldPoint.columns) {
            const gridColumnField = {
                id: gridColumn.id,
                SelectOption: this.getGridColumnFieldType(gridColumn.type),
                type: gridColumn.type,
                header: gridColumn.header,
                parentGridId: this.mapFieldPoint.id
            };
            fieldMap.set(gridColumn.id, gridColumnField);
        }

    }

    private convertMapFieldPointToGrid(mapFieldPoint: MapFieldPoint): any {
        return {
            Name: "Map Field Point",
            id: mapFieldPoint.id,
            SelectOption: "8", // Grid field type ID
            FieldType: {
                id: "8",
                text: "Grid",
                options: {
                    readOnly: false,
                    columns: [...mapFieldPoint.columns]
                },
                grid: true
            },
            breadcrumb: `${"Map Field Point"} > `,
            parent: `... > ${"Map Field Point"}`
        };
    }

    private filterObjectByProjectFields(obj: object): object {
        const filteredObj = {};

        for (const key in obj) {
            if (this.isRelevantField(key)) {
                filteredObj[key] = obj[key];
            }
        }

        return filteredObj;
    }

    private isRelevantField(fieldId: string): boolean {
        return this.fieldMap.has(fieldId) || fieldId.startsWith(DifferenceLogicUtility.MAP_FIELD_PREFIX);
    }

    private getFieldType(fieldId: string): string | null {
        const field = this.fieldMap.get(fieldId);
        return field ? field.SelectOption : null;
    }

    private configureMultipleValuesLogic(fieldId: string, filteredNewObject: object, filteredOldObject: object, fieldDelta?: any): void {
        const fieldType = this.getFieldType(fieldId);
        const newValue = filteredNewObject[fieldId];
        const oldValue = filteredOldObject[fieldId];
        const isMapFieldPoint = fieldId.startsWith(DifferenceLogicUtility.MAP_FIELD_PREFIX);
        const isGridType = fieldType === FieldType.Grid || isMapFieldPoint;

        if (this.isArrayFieldType(fieldType) || isMapFieldPoint) {
            const hasStructureChange = this.hasArrayStructureChange(newValue, oldValue);
            const isReordered = isGridType &&
                Array.isArray(newValue) && Array.isArray(oldValue) &&
                this.isArrayReordered(newValue, oldValue);

            if (hasStructureChange || isReordered) {
                this.multipleValueUpdate = true;
                this.jsonFormsId = fieldId;
                this.isRepositioningGrid = true;
            }
            else if (isGridType && Array.isArray(newValue) && Array.isArray(oldValue)) {
                if (this.checkForMultipleChangesInArrayItem(fieldDelta, newValue, oldValue)) {
                    this.hasMultipleArrayItemChanges = true;
                    return;
                }
                this.checkGridColumnsForMultipleValues(fieldId, newValue, oldValue);
            }
            else if (this.isArrayFieldType(fieldType) && Array.isArray(newValue) && Array.isArray(oldValue)) {
                this.multipleValueUpdate = true;
                this.jsonFormsId = fieldId;
            }
        }
    }

    private hasArrayStructureChange(newValue: any, oldValue: any): boolean {
        if (Array.isArray(newValue) !== Array.isArray(oldValue)) {
            return true;
        }

        if (!Array.isArray(newValue) || !Array.isArray(oldValue)) {
            return false;
        }

        if (newValue.length !== oldValue.length) {
            return true;
        }

        // Same length: check if the id composition changed (structural replace).
        const newIds = newValue.filter(item => item?.id).map(item => item.id);
        const oldIds = oldValue.filter(item => item?.id).map(item => item.id);
        if (newIds.length > 0 && oldIds.length > 0) {
            const oldIdSet = new Set(oldIds);
            return newIds.some(id => !oldIdSet.has(id));
        }

        return false;
    }

    private isArrayFieldType(fieldType: string | null): boolean {
        return fieldType !== null && DifferenceLogicUtility.ARRAY_FIELD_TYPES.includes(fieldType as FieldType);
    }

    private isArrayReordered(newArray: any[], oldArray: any[]): boolean {
        if (!Array.isArray(newArray) || !Array.isArray(oldArray) || newArray.length !== oldArray.length) {
            return false;
        }

        const newIds = newArray.filter(item => item?.id).map(item => item.id).sort();
        const oldIds = oldArray.filter(item => item?.id).map(item => item.id).sort();

        if (newIds.length !== oldIds.length || newIds.length === 0) {
            return false;
        }

        const sameItems = JSON.stringify(newIds) === JSON.stringify(oldIds);
        const differentOrder = JSON.stringify(newArray.map(item => item?.id)) !== JSON.stringify(oldArray.map(item => item?.id));

        return sameItems && differentOrder;
    }

    private getGridColumnFieldType(columnType: string): string | null {
        // Map grid column types to FieldType enum values for array type detection
        switch (columnType) {
            case 'Photo Gallery':
                return FieldType.PhotoGallery;
            case 'File Upload':
            case 'Uploader':
                return FieldType.Uploader;
            case 'Person':
                return FieldType.Person;
            case 'Text':
            case 'Text Box':
                return FieldType.TextBox;
            case 'Text Area':
                return FieldType.TextArea;
            case 'Date Select':
                return FieldType.DateSelect;
            case 'Checkbox':
            case 'True/False':
                return FieldType.CheckBox;
            case 'Drop Down List':
                return FieldType.DropDown;
            case 'Number':
                return FieldType.Number;
            case 'URL Hyperlink':
            case 'Web Link':
                return FieldType.URLHyperlink;
            case 'Email':
            case 'Email Hyperlink':
                return FieldType.EmailHyperlink;
            default:
                return null;
        }
    }

    private isNestedArrayType(fieldId: string): boolean {
        const field = this.fieldMap.get(fieldId);
        if (!field) {
            return false;
        }

        if (field.parentGridId && field.SelectOption) {
            return DifferenceLogicUtility.ARRAY_FIELD_TYPES.includes(field.SelectOption);
        }

        return false;
    }

    private checkGridColumnsForMultipleValues(gridFieldId: string, newGridData: any[], oldGridData: any[]): void {
        const gridField = this.fieldMap.get(gridFieldId);
        if (!gridField?.FieldType?.options?.columns) {
            return;
        }

        for (let rowIndex = 0; rowIndex < Math.max(newGridData.length, oldGridData.length); rowIndex++) {
            const newRow = newGridData[rowIndex] || {};
            const oldRow = oldGridData[rowIndex] || {};

            for (const gridColumn of gridField.FieldType.options.columns) {
                const columnId = gridColumn.id;
                const newCellValue = newRow[columnId];
                const oldCellValue = oldRow[columnId];

                if (!DifferenceLogicUtility.valuesAreTheSame(newCellValue, oldCellValue)) {
                    const columnFieldType = this.getGridColumnFieldType(gridColumn.type);

                    if (this.isArrayFieldType(columnFieldType)) {
                        this.multipleValueUpdate = true;
                        this.jsonFormsId = gridFieldId;

                        this.gridRowIndex = rowIndex;
                        this.gridColumnId = columnId;
                        this.gridRowId = newRow.id || oldRow.id;
                    }
                }
            }
        }
    }

    private setPathValidity(): void {
        const path = this.differenceObject.path;
        if (!path) {
            this.differenceObject.isPathValid = false;
            return;
        }

        const existsInOriginalSiteData = this.pathExistsInObject(this.originalSiteData || {}, path);
        this.differenceObject.isPathValid = existsInOriginalSiteData;
    }

    private pathExistsInObject(rootObject: any, path: string): boolean {
        if (!path?.startsWith('/')) {
            return false;
        }

        const segments = path.split('/');
        const normalizedSegments = segments[0] === '' ? segments.slice(1) : segments;

        if (normalizedSegments.length === 0) {
            return false;
        }

        if (normalizedSegments.some(segment => segment === '')) {
            return false;
        }

        return this.doesPathExistInObjectRecursive(rootObject, normalizedSegments);
    }

    private doesPathExistInObjectRecursive(currentObject: any, segments: string[]): boolean {
        // Base case: if there are no more segments to check, the path exists
        if (segments.length === 0) {
            return true;
        }

        const [segment, ...rest] = segments;
        const isNumericSegment = /^\d+$/.test(segment);

        if (currentObject === undefined || currentObject === null) {
            return false;
        }

        if (Array.isArray(currentObject)) {
            const arrayIndex = Number(segment);
            if (!Number.isInteger(arrayIndex) || arrayIndex < 0 || arrayIndex >= currentObject.length) {
                return false;
            }

            return this.doesPathExistInObjectRecursive(currentObject[arrayIndex], rest);
        }

        // Numeric segments are only valid for arrays, never object/string traversal.
        if (isNumericSegment) {
            return false;
        }

        if (typeof currentObject !== 'object') {
            return false;
        }

        if (rest.length === 0) {
            return true;
        }

        return this.doesPathExistInObjectRecursive(currentObject[segment], rest);
    }
}
